import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';

export interface FilterBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Título do sheet (default "Filtros"). */
  title?: string;
  children: React.ReactNode;
  /**
   * Breakpoint acima do qual o sheet NÃO renderiza (o desktop usa outro layout).
   * Default `'sm'` (< 640px). Telas cujo "mobile" vai até `lg` (ex.: Agenda, < 1024px)
   * passam `'lg'`.
   */
  hideAbove?: 'sm' | 'md' | 'lg';
  'data-testid'?: string;
}

/** Literais explícitos p/ o Tailwind gerar as classes (não interpolar). */
const HIDE_ABOVE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'sm:hidden',
  md: 'md:hidden',
  lg: 'lg:hidden',
};

/**
 * Bottom-sheet de filtros para MOBILE (`< sm`). Tema claro fixo (regra de
 * overlays). Renderiza apenas em telas pequenas (`sm:hidden`) — no desktop os
 * filtros continuam no popover ancorado ao botão da toolbar. Anatomia do mockup:
 * grab handle 38×4, header "Filtros" + fechar, conteúdo rolável.
 *
 * O conteúdo (`children`) é o MESMO das opções do popover desktop — extraia-o
 * para uma variável na página e passe aqui para não duplicar a lógica.
 */
export const FilterBottomSheet: React.FC<FilterBottomSheetProps> = ({
  isOpen,
  onClose,
  title = 'Filtros',
  children,
  hideAbove = 'sm',
  'data-testid': dataTestId,
}) => {
  // Light dismiss (DS §Overlays dismissal): superfície transiente fecha por
  // backdrop E por Esc.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return createPortal(
    <div className={`${HIDE_ABOVE[hideAbove]} fixed inset-0 z-[100]`} data-testid={dataTestId}>
      {/* backdrop */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(15,23,42,0.45)' }}
        onClick={onClose}
        aria-hidden
      />
      {/* sheet */}
      <div
        className="absolute inset-x-0 bottom-0 rounded-t-2xl px-2 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] max-h-[75vh] overflow-y-auto"
        style={{ backgroundColor: '#ffffff' }}
      >
        <div className="flex justify-center pt-2 pb-1">
          <span className="w-9 h-1 rounded-full" style={{ backgroundColor: '#e2e8f0' }} />
        </div>
        <div className="flex items-center justify-between px-2.5 pb-1">
          <span className="text-[15px] font-bold" style={{ color: '#0f172a' }}>{title}</span>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full inline-flex items-center justify-center"
            style={{ color: '#64748b' }}
            aria-label="Fechar"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};

FilterBottomSheet.displayName = 'FilterBottomSheet';
